import React, { useEffect, useRef } from 'react';

const AmbientBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Particle system
    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.2;
        this.color = Math.random() > 0.5 ? '#00BCD4' : '#9C27B0';
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        // Wrap around screen
        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Create particles
    const particles = Array.from({ length: 50 }, () => new Particle());

    // Liquid gradient blobs
    const drawLiquidGradient = () => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Create multiple gradient blobs
      const blobs = [
        { x: centerX + Math.sin(time * 0.001) * 200, y: centerY + Math.cos(time * 0.0015) * 150, radius: 400, color1: '#00BCD4', color2: '#9C27B0' },
        { x: centerX + Math.cos(time * 0.0012) * 250, y: centerY + Math.sin(time * 0.001) * 200, radius: 350, color1: '#9C27B0', color2: '#00BCD4' },
        { x: centerX - Math.sin(time * 0.0008) * 180, y: centerY - Math.cos(time * 0.001) * 180, radius: 300, color1: '#00BCD4', color2: '#1e1b4b' },
      ];

      blobs.forEach(blob => {
        const gradient = ctx.createRadialGradient(blob.x, blob.y, 0, blob.x, blob.y, blob.radius);
        gradient.addColorStop(0, blob.color1 + '15');
        gradient.addColorStop(0.5, blob.color2 + '08');
        gradient.addColorStop(1, 'transparent');

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      });
    };

    // Animation loop
    const animate = () => {
      // Clear with slight fade for trail effect
      ctx.fillStyle = 'rgba(11, 18, 32, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw liquid gradients
      drawLiquidGradient();

      // Update and draw particles
      ctx.globalAlpha = 1;
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      time++;
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.4 }}
    />
  );
};

export default AmbientBackground;
